import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Video, Library, ShieldUser, ArrowRight } from 'lucide-react'
import Navigation from './Navigation'

/**
 * AdvisorHub Component
 * 
 * Landing page for the Advisor Hub section, providing access to advisor-exclusive content.
 * 
 * Features:
 * - Role-based access control (visible only to advisor, investor, staff, admin roles)
 * - Two main content sections: Advisor Training Videos and Advisor Resources
 * - Card-based navigation to dedicated video and resource pages
 * - Professional gradient styling with ShieldUser icon
 * 
 * Routes:
 * - /advisor-hub - This landing page
 * - /advisor-hub/videos - Advisor Training Videos page
 * - /advisor-hub/resources - Advisor Resources page
 * 
 * Access Control:
 * - Protected by ProtectedRoute with allowedRoles=['advisor', 'investor', 'staff', 'admin']
 * - Founder roles (member_free, member_paid) are blocked from accessing this section
 */
const AdvisorHub = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ShieldUser className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Advisor Hub</h1>
              <p className="text-lg text-gray-600">Access exclusive advisor training content and resources</p>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Advisor Training Videos Card */}
          <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white rounded-3xl shadow-lg">
            <CardHeader>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">Advisor Training Videos</CardTitle>
              </div>
              <CardDescription className="text-base text-gray-600">
                Access exclusive advisor training calls and video content designed specifically for advisors.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/advisor-hub/videos">
                <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
                  Browse Videos
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Advisor Resources Card */}
          <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white rounded-3xl shadow-lg">
            <CardHeader>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center">
                  <Library className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">Advisor Resources</CardTitle>
              </div>
              <CardDescription className="text-base text-gray-600">
                Access exclusive guides, templates, and materials designed specifically for advisors.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/advisor-hub/resources">
                <Button className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
                  Browse Resources
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default AdvisorHub

